/**
 * Formatters - Utility Functions for All ML Infrastructure Pages
 * 
 * Provides consistent formatting for dates, numbers, bytes, etc.
 * 
 * Following BRAIN-PRE-CODE-CHECKLIST.md:
 * - Defensive null checks
 * - Consistent formatting patterns
 */

export const formatters = {
  /**
   * Format date to relative time (e.g., "2 hours ago")
   */
  formatRelativeTime(date: Date | string | null | undefined): string {
    if (!date) return 'Never';
    
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      const now = new Date();
      const diffMs = now.getTime() - dateObj.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} min ago`;
      
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      
      const diffMonths = Math.floor(diffDays / 30);
      if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
      
      const diffYears = Math.floor(diffMonths / 12);
      return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
    } catch {
      return String(date);
    }
  },

  /**
   * Format date to locale string
   */
  formatDateTime(date: Date | string | null | undefined): string {
    if (!date) return 'N/A';
    
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj.toLocaleString();
    } catch {
      return String(date);
    }
  },

  /**
   * Format bytes to human-readable size
   */
  formatBytes(bytes: number | null | undefined): string {
    if (bytes === null || bytes === undefined) return 'N/A';
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  },

  /**
   * Format number with commas
   */
  formatNumber(num: number | null | undefined): string {
    if (num === null || num === undefined) return 'N/A';
    return num.toLocaleString();
  },

  /**
   * Format percentage
   */
  formatPercentage(value: number | null | undefined, decimals: number = 1): string {
    if (value === null || value === undefined) return 'N/A';
    return `${value.toFixed(decimals)}%`;
  },

  /**
   * Format duration in milliseconds to human-readable
   */
  formatDuration(ms: number | null | undefined): string {
    if (ms === null || ms === undefined) return 'N/A';
    if (ms === 0) return '0ms';
    
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  },

  /**
   * Format accuracy/confidence score
   */
  formatScore(score: number | null | undefined): string {
    if (score === null || score === undefined) return 'N/A';
    return `${(score * 100).toFixed(1)}%`;
  },

  /**
   * Truncate text with ellipsis
   */
  truncate(text: string | null | undefined, maxLength: number = 50): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  }
};

export default formatters;
